import type { StateStorage } from 'zustand/middleware';

export const AUTH_STORAGE_KEY = 'auth-storage';
export const REMEMBER_ME_KEY = 'signin-remember-me';
export const REMEMBERED_EMAIL_KEY = 'signin-remembered-email';

/**
 * Returns whether the user opted into persistent auth.
 * Missing flag + existing local auth is treated as remembered (legacy behavior).
 */
export const getRememberMePreference = (): boolean => {
  if (typeof window === 'undefined') return true;

  const flag = window.localStorage.getItem(REMEMBER_ME_KEY);
  if (flag === 'true') return true;
  if (flag === 'false') return false;

  // Legacy sessions stored tokens in localStorage without a remember-me flag.
  return window.localStorage.getItem(AUTH_STORAGE_KEY) != null;
};

/**
 * Persist the remember-me preference and optional email prefill.
 * Clears auth from the opposite storage so only one session source remains.
 */
export const setRememberMePreference = (rememberMe: boolean, email?: string): void => {
  if (typeof window === 'undefined') return;

  if (rememberMe) {
    window.localStorage.setItem(REMEMBER_ME_KEY, 'true');
    if (email) {
      window.localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    }
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } else {
    window.localStorage.setItem(REMEMBER_ME_KEY, 'false');
    window.localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  }
};

export const clearAuthStorage = (): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
};

/**
 * Zustand storage adapter that routes auth persistence based on remember-me.
 * - Remember me ON  → localStorage (survives browser restart)
 * - Remember me OFF → sessionStorage (cleared when browser session ends)
 */
export const createAuthStateStorage = (): StateStorage => ({
  getItem: (name) => {
    if (typeof window === 'undefined') return null;

    if (getRememberMePreference()) {
      return window.localStorage.getItem(name) ?? window.sessionStorage.getItem(name);
    }

    return window.sessionStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;

    if (getRememberMePreference()) {
      window.localStorage.setItem(name, value);
      window.sessionStorage.removeItem(name);
      return;
    }

    window.sessionStorage.setItem(name, value);
    window.localStorage.removeItem(name);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(name);
    window.sessionStorage.removeItem(name);
  },
});
